import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@/lib/api-hooks';
import { Building2, Check, ChevronDown, Settings2 } from 'lucide-react';
import { backendApi } from '@/lib/backend';
import type { Id } from '../../../convex/_generated/dataModel';

const ACTIVE_CUSTOMER_KEY = 'yeastar_active_customer_id';

type CustomerNav = { id: string; name: string };

interface ClientSwitcherProps {
  /** Currently selected customer from session (staff activeCustomerId). */
  activeCustomer: { id: string; name: string } | null;
  isAdmin: boolean;
  /** Open the admin console (admin only). */
  onOpenAdmin?: () => void;
  /** True while setActiveCustomer is in flight. */
  className?: string;
}

export function ClientSwitcher({
  activeCustomer,
  isAdmin,
  onOpenAdmin,
  className = '',
}: ClientSwitcherProps) {
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const customersNav = useQuery(backendApi.tenancy.listCustomersNav, {}) as
    | CustomerNav[]
    | undefined;
  const setActiveCustomer = useMutation(backendApi.tenancy.setActiveCustomer);

  const customers = customersNav ?? [];
  const selectedId = activeCustomer?.id ?? '';
  const selectedName = activeCustomer?.name ?? (switching ? 'Switching…' : 'Select client');

  useEffect(() => {
    function onDocClick(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  // Auto-select first customer (or remembered) when staff has none.
  useEffect(() => {
    if (activeCustomer || !customers.length || switching) return;

    const remembered = localStorage.getItem(ACTIVE_CUSTOMER_KEY);
    const preferred =
      (remembered && customers.find((c) => c.id === remembered)?.id) || customers[0].id;

    setSwitching(true);
    void setActiveCustomer({ customerId: preferred as Id<'customers'> })
      .then(() => localStorage.setItem(ACTIVE_CUSTOMER_KEY, preferred))
      .finally(() => setSwitching(false));
  }, [activeCustomer, customers, setActiveCustomer, switching]);

  async function selectCustomer(customerId: string) {
    if (customerId === selectedId) {
      setOpen(false);
      return;
    }
    setSwitching(true);
    setOpen(false);
    try {
      await setActiveCustomer({ customerId: customerId as Id<'customers'> });
      localStorage.setItem(ACTIVE_CUSTOMER_KEY, customerId);
    } finally {
      setSwitching(false);
    }
  }

  return (
    <div ref={rootRef} className={`relative min-w-0 ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={switching && !customers.length}
        className="w-full max-w-[320px] flex items-center gap-3 pl-4 pr-3 py-1.5 min-h-[44px] rounded-full bg-white dark:bg-gray-800 hover:ring-1 hover:ring-gray-200 dark:hover:ring-gray-700 transition-colors text-left disabled:opacity-60"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Switch client"
      >
        <span className="relative flex h-2.5 w-2.5 shrink-0">
          <span
            className={`absolute inline-flex h-full w-full rounded-full opacity-75 ${
              activeCustomer ? 'bg-primary-400 animate-ping' : 'bg-amber-400'
            }`}
            style={{ animationDuration: '2s' }}
          />
          <span
            className={`relative inline-flex h-2.5 w-2.5 rounded-full ${
              activeCustomer ? 'bg-primary-500' : 'bg-amber-500'
            }`}
          />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-gray-900 dark:text-white truncate">
            {selectedName}
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
            <span className="font-medium uppercase tracking-wider">Client</span>
            {' · '}
            {switching
              ? 'Switching…'
              : activeCustomer
                ? 'Operating as this client'
                : customers.length
                  ? 'Choose a client'
                  : 'No clients yet'}
          </div>
        </div>
        <ChevronDown
          size={16}
          className={`shrink-0 text-gray-500 dark:text-gray-400 transition-transform ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>

      {open && (
        <div
          className="absolute top-full left-0 mt-2 z-50 w-[min(320px,calc(100vw-2rem))] rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl p-2 flex flex-col gap-0.5 max-h-[min(360px,50vh)] overflow-y-auto"
          role="listbox"
        >
          {customers.length === 0 ? (
            <div className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">
              {customersNav === undefined ? 'Loading clients…' : 'No active clients.'}
            </div>
          ) : (
            customers.map((customer) => {
              const isActive = customer.id === selectedId;
              return (
                <button
                  key={customer.id}
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  onClick={() => void selectCustomer(customer.id)}
                  className={`flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left w-full transition-colors ${
                    isActive
                      ? 'bg-primary-50 dark:bg-primary-900/40'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700/60'
                  }`}
                >
                  <Building2
                    size={15}
                    className={`shrink-0 ${
                      isActive
                        ? 'text-primary-600 dark:text-primary-400'
                        : 'text-gray-400 dark:text-gray-500'
                    }`}
                  />
                  <span
                    className={`flex-1 min-w-0 text-[13px] truncate ${
                      isActive
                        ? 'font-bold text-gray-900 dark:text-white'
                        : 'font-medium text-gray-700 dark:text-gray-200'
                    }`}
                  >
                    {customer.name}
                  </span>
                  {isActive && (
                    <Check size={15} className="shrink-0 text-primary-600 dark:text-primary-400" />
                  )}
                </button>
              );
            })
          )}

          {isAdmin && onOpenAdmin && (
            <>
              <div className="h-px my-1 mx-1 bg-gray-200 dark:bg-gray-700" />
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  onOpenAdmin();
                }}
                className="flex items-center gap-2 px-2.5 py-2 rounded-lg text-[12.5px] font-semibold text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30 w-full text-left"
              >
                <Settings2 size={14} />
                Admin Console
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
